import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SocialSidebar from '../components/layout/SocialSidebar';
import PageHero from '../components/layout/PageHero';
import ScrollReveal from '../components/ui/ScrollReveal';
import { companyInfo } from '../data/mockData';
import { Calendar, ArrowRight, User, Loader2 } from 'lucide-react';
import api from '../services/api';

const NewsPage = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Subscribe to real-time news updates
    const unsubscribe = api.news.subscribeToNews((newsData) => {
      setNews(newsData || []);
      setLoading(false);
    }, { published_only: true });

    // Cleanup subscription on unmount
    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>News & Events - {companyInfo.name}</title>
        <meta name="description" content="Stay updated with the latest news and events from Pride Path Commercial." />
      </Helmet>

      <Header />
      <SocialSidebar />
      <main className="pb-16">
        {/* Hero with background image */}
        <PageHero
          title="News & Events"
          subtitle={`Stay updated with the latest news and events from ${companyInfo.name}.`}
          breadcrumbs={[
            { label: 'Home', link: '/' },
            { label: 'News & Events' }
          ]}
          backgroundImage="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80"
        />

        <div className="max-w-7xl mx-auto px-4 py-12">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-12 h-12 text-[#2A9D8F] animate-spin" />
            </div>
          ) : news.length > 0 ? (
            <>
              {/* Featured Article (Latest) */}
              <ScrollReveal animation="fade-in" duration={500}>
                <div className="mb-12">
                  <article className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="grid grid-cols-1 lg:grid-cols-2">
                      <div className="h-64 lg:h-auto bg-gray-200">
                        {news[0].image_url ? (
                          <img
                            src={news[0].image_url}
                            alt={news[0].title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <span className="text-4xl font-bold opacity-25">Featured</span>
                          </div>
                        )}
                      </div>
                      <div className="p-8 flex flex-col justify-center">
                        <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
                          <span className="flex items-center">
                            <Calendar size={14} className="mr-1" />
                            {new Date(news[0].date || news[0].created_at).toLocaleDateString()}
                          </span>
                          <span className="flex items-center">
                            <User size={14} className="mr-1" />
                            {news[0].author || 'Admin'}
                          </span>
                        </div>
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                          {news[0].title}
                        </h2>
                        <p className="text-gray-600 mb-6">
                          {news[0].summary}
                        </p>
                        <Link
                          to={`/news/${news[0].slug}`}
                          className="inline-flex items-center text-[#2A9D8F] font-semibold hover:underline"
                        >
                          Read Full Article
                          <ArrowRight size={18} className="ml-2" />
                        </Link>
                      </div>
                    </div>
                  </article>
                </div>
              </ScrollReveal>

              {/* News Grid (Rest) */}
              {news.length > 1 && (
                <ScrollReveal animation="fade-up" delay={200}>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {news.slice(1).map((item) => (
                      <article
                        key={item.id}
                        className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow group flex flex-col h-full"
                      >
                        <div className="relative h-52 overflow-hidden bg-gray-200">
                          {item.image_url ? (
                            <img
                              src={item.image_url}
                              alt={item.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <span>No Image</span>
                            </div>
                          )}
                          <div className="absolute top-3 left-3 bg-[#2A9D8F] text-white text-xs px-3 py-1 rounded-full flex items-center">
                            <Calendar size={12} className="mr-1" />
                            {new Date(item.date || item.created_at).toLocaleDateString()}
                          </div>
                        </div>

                        <div className="p-6 flex-grow flex flex-col">
                          <h3 className="font-semibold text-lg text-gray-900 mb-3 line-clamp-2 group-hover:text-[#2A9D8F] transition-colors">
                            {item.title}
                          </h3>
                          <p className="text-gray-500 text-sm line-clamp-3 mb-4 flex-grow">
                            {item.summary}
                          </p>
                          <Link
                            to={`/news/${item.slug}`}
                            className="text-[#2A9D8F] font-medium flex items-center text-sm hover:underline mt-auto"
                          >
                            Read More
                            <ArrowRight size={14} className="ml-1" />
                          </Link>
                        </div>
                      </article>
                    ))}
                  </div>
                </ScrollReveal>
              )}

              {/* Load More Button - Logic to simulate pagination or load more from API could be added here, 
                                but hiding for now since we fetch all */}
              {/* <div className="mt-12 text-center">
                                <button className="border-2 border-[#2A9D8F] text-[#2A9D8F] px-8 py-3 rounded-lg font-medium hover:bg-[#2A9D8F] hover:text-white transition-colors">
                                    Load More Articles
                                </button>
                            </div> */}
            </>
          ) : (
            <div className="text-center py-20 bg-white rounded-lg shadow-sm">
              <h3 className="text-xl font-medium text-gray-500">No news articles found.</h3>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default NewsPage;
